import _ from 'lodash';

export function hex2RGB(hex) {
  const rgbChar = ['r', 'g', 'b'];

  const normal = hex.match(/^#([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})$/i);
  if (normal) {
    return normal.slice(1).reduce((a, e, i) => {
      a[rgbChar[i]] = parseInt(e, 16);
      return a;
    }, {});
  }

  const shorthand = hex.match(/^#([0-9a-f])([0-9a-f])([0-9a-f])$/i);
  if (shorthand) {
    return shorthand.slice(1).reduce((a, e, i) => {
      a[rgbChar[i]] = 0x11 * parseInt(e, 16);
      return a;
    }, {});
  }

  return { r: 0, g: 0, b: 0 };
}

export function getColorPalette(hex) {
  try {
    const { r, g, b } = hex2RGB(hex);

    const red = parseInt(r, 10) * 299;
    const green = parseInt(g, 10) * 587;
    const blue = parseInt(b, 10) * 114;

    const color = Math.round((red + green + blue) / 1000);

    const textColor = color > 125 ? 'black' : 'white';
    const backColor = `rgb(${r},${g},${b})`;

    return { textColor, backColor };
  } catch (e) {
    return {};
  }
}

export const getLightBackground = (hex, lightness = 0.8) => {
  if (!hex) return { background: 'transparent' };

  const rgb = parseInt(hex.slice(1), 16);
  const r = (rgb >> 16) & 255;
  const g = (rgb >> 8) & 255;
  const b = rgb & 255;

  const lighten = (c) => Math.round(c + (255 - c) * lightness);
  const toHex = (r, g, b) => `#${[r, g, b].map((x) => x.toString(16).padStart(2, '0')).join('')}`;

  return {
    color: hex,
    background: toHex(lighten(r), lighten(g), lighten(b)),
  };
};

export function setupInlineStyle(attrs) {
  const rules = [
    attrs?.mainColor &&
      `
      .ant-slider-track,
      .ant-picker-footer button,
      .ant-switch.ant-switch-checked,
      .ace-form-field-wrapper .ace-form-field-underline.ace-form-field-should-float::before,
      .ant-picker-cell-in-view.ant-picker-cell-selected .ant-picker-cell-inner,
      .ant-checkbox-checked .ant-checkbox-inner,
      .ant-spin-dot.ant-spin-dot-spin .ant-spin-dot-item,
      .ant-radio-inner::after,
      .ant-tabs-ink-bar {
        background-color: ${attrs.mainColor} !important;
      }
      .ant-picker-suffix,
      .ace-input-element [class^='automy-icon-']::before,
      .ant-picker-footer a,
      .antd-pro-components-common-header-actions-style-actionBtn,
      .ant-select-item-option-selected:not(.ant-select-item-option-disabled) .ant-select-item-option-state,
      .antd-pro-components-filters-page-size-style-pageSizeFilter [class^='automy-icon-']::before,
      .ant-tabs-tab.ant-tabs-tab-active .ant-tabs-tab-btn,
      .ant-spin-text {
        color: ${attrs.mainColor} !important;
      }
      .ant-slider-handle:hover,
      .ant-picker-cell-in-view.ant-picker-cell-today .ant-picker-cell-inner::before,
      .antd-pro-components-common-header-actions-style-actionBtn,
      .ant-checkbox-checked .ant-checkbox-inner,
      .ant-checkbox-wrapper:visited .ant-checkbox-inner,
      .ant-checkbox-wrapper:active .ant-checkbox-inner,
      .ant-checkbox-wrapper:target .ant-checkbox-inner,
      .ant-checkbox-wrapper:focus-within .ant-checkbox-inner,
      .ant-checkbox-wrapper:hover .ant-checkbox-inner,
      .ant-checkbox-checked::after,
      .ant-radio-checked .ant-radio-inner,
      .ant-radio-checked::after {
        border-color: ${attrs.mainColor} !important;
      }
      .antd-pro-components-common-header-actions-style-actionBtn.antd-pro-components-common-header-actions-style-export,
      .antd-pro-components-common-header-actions-style-actionBtn.antd-pro-components-common-header-actions-style-import,
      .ant-select-item.ant-select-item-option.ant-select-item-option-active,
      .ant-select-item-option-selected:not(.ant-select-item-option-disabled) {
        background-color: ${attrs.mainColor}2a !important;
      }
      .ant-upload-drag-icon svg circle,
      .ant-camera-input-container svg circle {
        fill: ${attrs.mainColor}2a !important;
      }
      .ant-camera-input-container button:not(:disabled) .ant-camera-icon {
        background-color: ${attrs.mainColor}2a !important;
        svg {
          color: ${attrs.mainColor} !important;
          fill: ${attrs.mainColor} !important;
        }
      }
      .antd-pro-components-common-signature-draw-style-canvasWrapper .ant-btn svg g path,
      .ant-select-arrow svg g rect,
      .ant-input-affix-wrapper.ant-select-dropdown-input .ant-input-prefix svg g rect,
      .ant-select-clear svg path {
        fill: ${attrs.mainColor} !important;
      }
      .ant-upload-drag-icon svg path,
      .ant-upload-drag-icon svg g path,
      .ant-camera-input-container svg g path {
        stroke: ${attrs.mainColor} !important;
      }
      .ant-select-dropdown .ant-select-dropdown-input {
        border-color: ${attrs.mainColor} !important;
        box-shadow: 0 0 0 2px ${attrs.mainColor}2a !important;
      }
      .antd-pro-components-form-table-record-form-style-recordForm .btn-group {
        .cancel-btn,
        .add-btn:not(:disabled) {
          border-color: ${attrs.mainColor};
        }
        .cancel-btn {
          color: ${attrs.mainColor};
        }
        .add-btn:not(:disabled) {
          color: ${attrs.textColor};
          background-color: ${attrs.mainColor};
        }
      }
      .antd-pro-components-common-file-viewer-style-pdfActions button {
        color: ${attrs.textColor} !important;
        background-color: ${attrs.backColor} !important;
        border-color: ${attrs.backColor} !important;
        padding: 16px 32px !important;
        border-radius: 8px !important;
        height: auto !important;
      }
    `,
  ].filter(Boolean);

  if (rules.length > 0) {
    const style = document.createElement('style');
    const head = document.head || document.getElementsByTagName('head')[0];
    head.appendChild(style);
    style.setAttribute('class', 'custom-styles');
    style.setAttribute('type', 'text/css');
    style.appendChild(document.createTextNode(rules.join('')));
  }
}

export function setupDocumentFont({ fontUrl, font }) {
  const link = document.createElement('link');

  link.setAttribute('rel', 'preload');
  link.setAttribute('href', fontUrl);
  link.setAttribute('as', 'style');
  link.setAttribute('class', 'custom-fonts');

  document.head.appendChild(link);
  link.setAttribute('rel', 'stylesheet');
  document.head.appendChild(link);

  const styles = document.querySelector('.custom-styles');
  if (styles) {
    const rules = `
    .antd-pro-components-form-form-field-groups-style-h3,
    .ace-form-field-label,
    .ant-form-item-label > label,
    .ant-upload-input-label,
    .ant-upload-text,
    .empty-text,
    .antd-pro-components-filters-page-size-style-title,
    .ant-check-group-label,
    .ant-radio-group-label,
    .ant-checkbox-wrapper-in-form-item span + span,
    .ant-radio-wrapper-in-form-item span + span,
    .ant-canvas-draw-label,
    .ant-camera-input-label,
    .antd-pro-components-common-file-viewer-style-pdfActions button {
      font-family: ${font} !important;
    }`;
    styles.appendChild(document.createTextNode(rules));
  }
}

export const getFontFamilyFromUrl = (fontUrl) => {
  const fontUrlTokens = fontUrl?.replace(/([ :.,;=/?@&]+)/g, '§sep§').split('§sep§');
  const familyPosition = fontUrlTokens?.findIndex((e) => e.toLowerCase() === 'family');
  return familyPosition > -1 ? fontUrlTokens?.[familyPosition + 1] : '';
};

export function wireUpInlineStyles(form) {
  const nextState = {};

  const attrs = _.pick({ ...form?.config, ...form?.style, ...form?.publicStyle }, [
    'description',
    'fontUrl',
    'header',
    'logoUrl',
    'mainColor',
    'template',
    'title',
    'name',
  ]);

  if (attrs?.mainColor) {
    Object.assign(attrs, getColorPalette(attrs?.mainColor));
  }

  Object.assign(nextState, {
    tplProps: {
      ...attrs,
      title: attrs?.title || attrs?.name || form?.name,
      logoUrl: attrs?.logoUrl || attrs?.header?.image?.downloadUrl,
      description: attrs?.description || attrs?.header?.text,
    },
  });

  setupInlineStyle(attrs);

  if (attrs?.fontUrl) {
    const font = getFontFamilyFromUrl(attrs?.fontUrl);
    setupDocumentFont({ ...attrs, font });
    Object.assign(nextState, { fontFamily: font });
  }

  return nextState;
}
